/**
 * SelectionState store (Agent Air, Wave 2) — the ONE selected product template
 * that Batch + Export render against.
 *
 * A selection carries everything the render path needs:
 *   - templateId / versionId        → job identity
 *   - templateVersion (wire)        → real geometry for the engine adapter
 *   - context (slugs + display)     → buildRef paths + the export sheet
 *
 * Every selection is backed by a REAL catalog template with real geometry. A
 * template with no saved geometry cannot be selected — `selectionFromTemplate`
 * throws a clear error rather than substituting placeholder geometry.
 */
import { create } from "zustand";
import type { WireTemplateVersion } from "@genz/engine-bind";
import type { ProductTemplate } from "@genz/platform";
import { hasAnyBakedView, type BakedAssetRefsMap } from "../templates/bakedSync.js";
import { catalogSlug, resolveProductNames, type CatalogNameLists } from "../catalog/names.js";
import { isMockupOnlyTemplate } from "../templates/wireMasks.js";

/** Slug + display context (buildRef parts + the export sheet). */
export interface SelectionContext {
  customer: string;
  product: string;
  phoneModel: string;
  caseStyle: string;
  phoneSlug: string;
  caseStyleSlug: string;
  ext: "jpg" | "png" | "webp";
  /**
   * Real human labels for the mockups export tree (Phase 5 funnel). When the Mockup
   * Generator funnel drives the selection it supplies the TRUE Product Type / Brand /
   * Model it picked, so `buildRef({scheme:"mockups", …})` emits e.g.
   * `Mockups/Transparent Phone Cases/OnePlus/OnePlus 6/<View>/<Design>.<ext>`.
   * Omitted for the classic "Use for batch" entry — the BatchPanel then derives the
   * folder labels from the slugs (graceful fallback), so nothing regresses.
   */
  productType?: string;
  brand?: string;
  model?: string;
  /** Seller brand chosen in the funnel's step 4 — carried so the Batch tab pre-selects
   *  the logo overlay instead of the pick silently doing nothing. */
  sellerBrandId?: string;
}

export interface TemplateSelection {
  templateId: string;
  versionId: string;
  /** Human label for the selected template (sidebar / panel headings). */
  name: string;
  /**
   * The template's human CODE ("IM-16P-MAT"), when the server has one.
   *
   * Carried for ONE reason: two templates staged in the same run can share a name, and the
   * folder segment that separates them used to be the whole template id — a raw UUID on
   * disk (review-2 item 3). The code is scope-unique server-side and the operator already
   * reads it on every row, so it is the right disambiguator.
   */
  code?: string | null;
  /** Canonical wire version (real geometry) the engine renders from. */
  templateVersion: WireTemplateVersion;
  context: SelectionContext;
  /**
   * WS-P / AUD-8 LB-7: does the PUBLISHED version declare a baked mockup?
   *
   * Read straight off the catalog row that built this selection
   * (`hasAnyBakedView(currentVersion.bakedAssets)` — the same predicate the cloud's
   * `has_baked_assets` uses, so client and server agree by construction). Forwarded to
   * `EnqueueInput.bakeExpected`, where it decides whether a render with no locally-resolved
   * bake is a sync failure or a legitimate classic template. The row is already in hand here,
   * so this costs no fetch.
   *
   * LEDGER — why this is NOT `bakedAssets || base_case_image_ref`, and what stays open.
   *
   * A published-bake-less row that carries a case photo looks exactly like a template whose
   * bake never uploaded, so widening the predicate to the case photo seems to close the
   * blank-mock-up class. It does not: `TemplateEditor.saveDraft` BACKFILLS
   * `base_case_image_ref` on every Save, and every legacy classic row in the live catalog
   * already carries one alongside polygon masks and `baked_assets = null`. Widening it makes
   * those rows fail `bake-missing` on EVERY device, and the message's remedy ("Force sync
   * all") cannot fix a bake that does not exist — an unrecoverable refusal. The cloud takes
   * the same position deliberately: `cloud/app/catalog/baked.py`'s
   * `BAKELESS_APPROVAL_WARNING` warns and never blocks, because a genuinely classic template
   * must stay approvable.
   *
   * The class this guard was widened for is closed at its SOURCE instead: the wizard now
   * refuses to publish when the bake cannot be uploaded (F-1), so no new row of that shape is
   * created.
   *
   * CLOSED (was: "the classic render never composites the case photo at all"). It does now, and
   * it closed the way this ledger asked — by making the render honest, not by refusing the rows.
   * `RenderRequest.baseImage` carries the PIXELS the wire cannot (`stores/caseImage.ts` resolves
   * `base_case_image_ref` through the hosted ref, then this device's local image store) and
   * `render::render_one_with_base` composites the design over them. A device that cannot resolve
   * the photo still renders, byte-identically to before, and says so in the job trace. So the
   * predicate above stays exactly as it is: the case photo is still not evidence of a bake, and
   * a bake-less row is still renderable — it just renders the product now.
   */
  bakeExpected?: boolean;
}

interface SelectionState {
  selection?: TemplateSelection;
  /**
   * Templates explicitly queued for batch via "Use this template for batch"
   * (Templates → Batch). The batch renders the cartesian product of THESE
   * templates × the uploaded designs into one export queue. This is the ONLY
   * path that adds a template to the batch — merely opening/editing one does not.
   */
  batchSelections: TemplateSelection[];
  /**
   * Tree positions (`product::brand::modelKey`) whose Active-template auto-check has
   * already been offered this session. It lives HERE, not in BatchContents' component
   * state, because that state dies with the pane: leaving the funnel and coming back
   * silently re-added the Active template the operator had deliberately unticked.
   *
   * Scoped to the CURRENT staged set — `clearBatch` empties it. Outliving the set made the
   * opposite failure: after "Clear batch" the same model never auto-checked again.
   */
  autoCheckedKeys: string[];
  /** Pick a real template (resolved from the catalog) — drives Export's context. */
  select(selection: TemplateSelection): void;
  /** Reset the single selection (Export shows the empty state). */
  clear(): void;
  /**
   * Queue a template for batch (dedup by templateId; last write wins so a re-
   * edited template carries fresh geometry). Also sets it as the single
   * `selection` so Export has a context to build its sheet against.
   */
  addToBatch(selection: TemplateSelection): void;
  /** Remove one template from the batch queue (by templateId). */
  removeFromBatch(templateId: string): void;
  /** Clear the whole batch queue (Batch shows the empty state) AND the auto-check guard —
   *  the guard describes the set that was just discarded (see {@link autoCheckedKeys}). */
  clearBatch(): void;
  /**
   * Apply the funnel's seller-brand pick to EVERY already-staged template (and the
   * `selection` singleton). The Select tab auto-checks a model's Active template BEFORE
   * the operator ever reaches the brand dropdown, so a pick that only rode along with
   * templates ticked AFTERWARDS meant the default flow rendered with no logo at all —
   * while the dropdown still showed the brand. Pass `undefined` for "no brand logo".
   */
  setSellerBrand(sellerBrandId: string | undefined): void;
  /** Record that this tree position's Active-template auto-check has been offered. */
  markAutoChecked(key: string): void;
}

export const useSelectionStore = create<SelectionState>((set) => ({
  selection: undefined,
  batchSelections: [],
  autoCheckedKeys: [],
  select: (selection) => set({ selection }),
  clear: () => set({ selection: undefined }),
  addToBatch: (selection) =>
    set((s) => ({
      selection,
      batchSelections: [
        ...s.batchSelections.filter((b) => b.templateId !== selection.templateId),
        selection,
      ],
    })),
  removeFromBatch: (templateId) =>
    set((s) => ({ batchSelections: s.batchSelections.filter((b) => b.templateId !== templateId) })),
  // The auto-check guard means "already offered FOR THIS STAGED SET", so it dies with the
  // set. Nothing ever cleared it once it moved into the store, so after "Clear batch" the
  // operator could reopen the same model and get NO Active-template default — an empty
  // batch with nothing staged, for the rest of the process lifetime.
  clearBatch: () => set({ batchSelections: [], autoCheckedKeys: [] }),
  setSellerBrand: (sellerBrandId) =>
    set((s) => {
      const withBrand = (sel: TemplateSelection): TemplateSelection => ({
        ...sel,
        context: { ...sel.context, sellerBrandId },
      });
      return {
        selection: s.selection ? withBrand(s.selection) : undefined,
        batchSelections: s.batchSelections.map(withBrand),
      };
    }),
  markAutoChecked: (key) =>
    set((s) => (s.autoCheckedKeys.includes(key)
      ? s
      : { autoCheckedKeys: [...s.autoCheckedKeys, key] })),
}));

// ---------------------------------------------------------------------------
// Resolving a real selection from the catalog.
// ---------------------------------------------------------------------------

/**
 * Build a `TemplateSelection` from a catalog `ProductTemplate`. The version's wire
 * payload supplies the real render geometry. A template whose version has no saved
 * geometry cannot render — we throw a clear error rather than substituting
 * placeholder geometry (callers surface it to the user).
 *
 * F-7: the display context is resolved through `catalog/names`, NOT read off the ids.
 * `phoneModelId` / `caseStyleId` are catalog UUIDs; seeding `phoneModel`/`caseStyle` (and
 * therefore the slugs, and therefore the SKU and the export folder tree) from them is what
 * wrote `Mockups/TPU Phone Case/040d67ee/040d67ee 2b53 42b1 8ec3 B5fc1f6a2580/…` and put bare
 * UUIDs in the sheet, the SKU and the Batch chip. Pass `lists` (the phone-model / case-style
 * catalogs the caller already loads for the wizard) to resolve the OPERATOR-chosen names;
 * without them the resolver still degrades to the server display fields, then the template's
 * own name, then a short id — never a raw identifier.
 */
export function selectionFromTemplate(
  t: ProductTemplate,
  customerId: string,
  lists?: CatalogNameLists,
): TemplateSelection {
  const v = t.currentVersion;
  if (isMockupOnlyTemplate(t)) {
    throw new Error(`Template "${t.name}" is a mock-up only template with no print area — it cannot take a design.`);
  }
  if (!v.payload) {
    throw new Error(
      `Template "${t.name}" has no saved geometry yet — open it, run detection ` +
      `and submit a version before using it for batch.`,
    );
  }
  // The human names behind the catalog ids — one resolver, shared by the folder tree, the
  // sheet columns, the SKU and the Batch chip (see `catalog/names`).
  const names = resolveProductNames(t, lists);
  // Display fields (P3) feed the export sheet's product label + extension. The
  // server resolves `category` (== product_type); the output format is the
  // primary render format. Slugs derive from the resolved NAMES, never from the ids.
  const product = t.display?.category ?? "phone-case";
  const ext = (t.display?.outputFormat as SelectionContext["ext"]) ?? "jpg";
  // Client status (Draft | Approved | Active | Archived) → wire status
  // (Draft | Pending | Active). The status stays HONEST (a Draft is reported as a
  // Draft, so approval/publishing UX is unchanged).
  const wireStatus: WireTemplateVersion["status"] = v.status === "Draft" ? "Draft" : "Active";
  const templateVersion: WireTemplateVersion = {
    id: v.versionId,
    template_id: t.templateId,
    version: v.versionNumber,
    status: wireStatus,
    // `locked` gates the engine render. Client rendering is always the OWNER's own
    // LOCAL preview/export (there is no server render — Constitution III), so a Draft
    // with real geometry must still render; the cloud's approval flow — not the engine
    // lock — governs what gets published/shared. So always render-locked locally.
    locked: true,
    payload: v.payload,
  };
  return {
    templateId: t.templateId,
    versionId: v.versionId,
    name: t.name,
    code: t.code,
    templateVersion,
    // WS-P: the catalog row already knows whether this version PUBLISHED a bake. Declare it
    // here rather than making the render path infer it from polygon presence (AUD-8 LB-7).
    bakeExpected: hasAnyBakedView(v.bakedAssets as BakedAssetRefsMap | null | undefined),
    context: {
      customer: customerId,
      product,
      phoneModel: names.model,
      caseStyle: names.caseStyle,
      phoneSlug: names.phoneSlug,
      caseStyleSlug: names.caseStyleSlug,
      ext,
      // The mockups folder tree's Brand/Model. The Mockup Generator funnel overlays its own
      // picks on top (`selectionFromFunnel`); every OTHER entry point — "Use for batch" from
      // the list, "Render" from the editor — reaches the tree through these, and BatchPanel
      // used to derive them by splitting the phone SLUG, which is how the id's own 8-char
      // prefix became a folder nested above the full id. An unknown brand stays UNDEFINED so
      // `buildRef` skips the segment rather than guessing one.
      brand: names.brand || undefined,
      model: names.model,
    },
  };
}

/** Real human labels the Mockup Generator funnel knows (Phase 5). */
export interface FunnelLabels {
  /** Product Type folder, e.g. "Transparent Phone Cases" (from the chosen product type). */
  productType?: string;
  /** Brand folder, e.g. "OnePlus" (the selected phone brand). */
  brand?: string;
  /** Model folder, e.g. "OnePlus 6" (the selected device model). */
  model?: string;
  /** Phone slug for the mockups tree/SKU; falls back to slugify(model) when absent. */
  phoneSlug?: string;
  /** Seller brand chosen in step 4, carried so the Batch tab pre-selects the logo. */
  sellerBrandId?: string;
}

/**
 * Build a `TemplateSelection` from a catalog template PLUS the funnel's real labels.
 * Reuses {@link selectionFromTemplate} for all geometry/slug resolution, then overlays
 * the funnel-known Product Type / Brand / Model so the mockups export tree uses the true
 * human folders rather than labels derived from slugs. Fields the funnel doesn't supply
 * are left off (BatchPanel then falls back to its slug-derived labels).
 *
 * `lists` matters even though the funnel supplies its own Brand/Model (review-2 item 1): the
 * funnel's labels drive the FOLDER tree only. The sheet's `Phone Model` / `Case Style`
 * columns and the SKU come from `phoneModel` / `caseStyle` / the slugs, which only the
 * resolver fills — so without the lists a funnel run wrote a correct folder tree and a sheet
 * whose product columns said the TEMPLATE's name (`ONEPLUS-6-TEMPLATE-CASE-MAGENTA-ART`).
 */
export function selectionFromFunnel(
  t: ProductTemplate,
  customerId: string,
  labels: FunnelLabels,
  lists?: CatalogNameLists,
): TemplateSelection {
  const base = selectionFromTemplate(t, customerId, lists);
  return {
    ...base,
    context: {
      ...base.context,
      // A funnel-supplied phone slug keeps the mockups tree aligned with the picked model
      // (the base slug is derived from the template's own model label, which can differ).
      phoneSlug: labels.phoneSlug?.trim() ? catalogSlug(labels.phoneSlug) : base.context.phoneSlug,
      productType: labels.productType?.trim() || undefined,
      // A label the funnel does not supply keeps the RESOLVED one from `selectionFromTemplate`
      // (F-7). It used to be overwritten with `undefined`, which handed BatchPanel back to its
      // slug-splitting guess — the path that produced the id-derived Brand folder.
      brand: labels.brand?.trim() || base.context.brand,
      model: labels.model?.trim() || base.context.model,
      sellerBrandId: labels.sellerBrandId?.trim() || undefined,
    },
  };
}
